/**
 * CM Bet Ticker domain — Lab `/bet-ticker` + `/bet-ticker-previous` + `/bet-ticker-flag-final`.
 * Row shape matches CM-API `BookTickerRow` (incl. flag/comment).
 */

/** Server caps a page at 100; default matches Agent ticker. */
export const TICKER_PAGE_SIZES = [25, 50, 100] as const

export type TickerStatusScope = 'current' | 'previous'
export type BetSide = 'BACK' | 'LAY'

export const CURRENT_TICKER_STATUSES = [
  'EXECUTABLE',
  'EXECUTION_COMPLETE',
  'PENDING',
  'ACCEPTED',
  'MATCHED',
  'PARTIALLY_MATCHED',
  'UNMATCHED',
] as const

export const PREVIOUS_TICKER_STATUSES = [
  'WINNER_DECLARED',
  'WINNER_DECLARED_CANCELLED',
  'WINNER_DECLARED_EXECUTABLE',
  'REVERSED_WINNER',
  'CANCELLED',
  /** Result-API settle-match-odds (incl. Betfair Line Session). */
  'SETTLED',
  /** Lab VAR void / Result-API invalid — show on Bet Ticker Previous. */
  'VOID_VAR',
  'INVALID_BET',
  'RT_INVALID_BET',
] as const

export const TICKER_STATUS_LABEL: Record<string, string> = {
  EXECUTABLE: 'Executable',
  EXECUTION_COMPLETE: 'Complete',
  PENDING: 'Pending',
  ACCEPTED: 'Accepted',
  MATCHED: 'Matched',
  PARTIALLY_MATCHED: 'Partial',
  UNMATCHED: 'Unmatched',
  WINNER_DECLARED: 'Winner',
  WINNER_DECLARED_CANCELLED: 'Winner cancelled',
  WINNER_DECLARED_EXECUTABLE: 'Winner executable',
  REVERSED_WINNER: 'Reversed',
  CANCELLED: 'Cancelled',
  SETTLED: 'Settled',
  VOID_VAR: 'Void VAR',
  INVALID_BET: 'Invalid bet',
  RT_INVALID_BET: 'RT invalid bet',
}

export function tickerStatusesForScope(scope: TickerStatusScope): readonly string[] {
  return scope === 'previous' ? PREVIOUS_TICKER_STATUSES : CURRENT_TICKER_STATUSES
}

export function tickerStatusLabel(status: string): string {
  return TICKER_STATUS_LABEL[status] ?? status
}

export interface TickerBet {
  id: string
  placedAt: string | null
  matchedAt: string | null
  memberId: string
  memberUsername: string
  sport: string
  eventName: string
  marketId: string
  marketName: string
  selection: string
  side: BetSide
  currency: string
  /** Legacy single odds — LS = Odds Placed (runs); else matched. */
  odds: number
  /** Lab Odds Placed. */
  oddsPlaced: number
  /** Lab Odds Matched. */
  oddsMatched: number
  matchedAmount: number
  unmatchedAmount: number
  /** Lab Profit/Liability. */
  profitLiability: number
  /** Lab Amount Placed on Betfair. */
  amountPlacedOnBetfair: number
  amountExecuted: number
  amountPending: number
  amountCancelled: number
  multiplier: number
  clientIp: string | null
  /** Lab getAllBets `smartUser` — green row text (text-blue #00ff2b). */
  smartUser: boolean
  /**
   * Lab getAllBets `isSmartUserByCron` — cron device-link flag.
   * Member cell shows comment icon + title=`smartUserComment` (Lab fa-comments-o).
   */
  isSmartUserByCron: boolean
  /** Lab getAllBets `smartUserComment` — e.g. Linked to user X. */
  smartUserComment: string | null
  /** Lab getAllBets `doubtfull` — yellow row text (text-warnings #f4dc11). */
  doubtfull: boolean
  /** Lab getAllBets `watchlist` — red row text (text-danger). */
  watchlist: boolean
  isFlagged: boolean
  isFinalFlagged: boolean
  isFlaggedBy: string
  isFinalFlaggedBy: string
  comment: string
  finalFlagCategory: string
  /** Lab `reason` — IncorrectPrice / Sniping / LowLiquidity. */
  reason: string
  /** Lab `isBetfairFlagged` — vendor flag; drives reason circle + row tint. */
  isBetfairFlagged: boolean
}

export interface TickerPage {
  rows: TickerBet[]
  total: number
  page: number
  pageSize: number
  asOf: string
}

export interface TickerFilterOptions {
  sports: string[]
  events: string[]
  markets: string[]
}

export interface TickerFilters {
  /** Lab Select TM — CM-API `tmId`. */
  tmId: string
  /**
   * Lab multi ui-select `formData.userName` — exact match list.
   * URL key `member` (repeatable). Sent as `userName: string[]`.
   */
  members: string[]
  /**
   * Legacy free-text substring (`q`). Kept for old URLs; Lab UI is multi-select.
   * Used only when `members` is empty → `userName: string` (LIKE).
   */
  search: string
  /**
   * Lab Sub Game → CM-API `eventType` (filter-options.sports).
   * URL key stays `sport`.
   */
  sport: string
  event: string
  /** Specific market name → CM-API `marketName`. */
  market: string
  /**
   * Lab Market Types (Match Odds / Bookmaker / Fancy / Linesession).
   * Narrows the market picker; see `marketNameForRequest`.
   */
  marketType: string
  side: BetSide | 'all'
  status: string
  currency: string
  /** Lab Flagged / Flagged Final filter → CM-API `flaggedBets` / `finallyFlaggedBets`. */
  flagged: 'all' | 'flagged' | 'final'
  from: string
  to: string
  stakeMin: number | null
  stakeMax: number | null
  page: number
  pageSize: number
}

export type TickerPageSize = (typeof TICKER_PAGE_SIZES)[number]

/** Lab bet-ticker currency filter (INR / POINTS / POINT@1). */
export const TICKER_CURRENCIES = ['INR', 'POINTS', 'POINT@1'] as const

export type TickerCurrency = (typeof TICKER_CURRENCIES)[number]

/** Lab final-flag category chips (bet-ticker.html) — must match CM-API. */
export const FINAL_FLAG_CATEGORIES = [
  '4 6 wicket bets',
  'matching bets',
  'snipping bets',
  'major bets',
  'group betting',
] as const

export type FinalFlagCategory = (typeof FINAL_FLAG_CATEGORIES)[number]

/** Short chip labels for final-flag categories. */
export const FINAL_FLAG_CATEGORY_LABEL: Record<FinalFlagCategory, string> = {
  '4 6 wicket bets': '4',
  'matching bets': 'M',
  'snipping bets': 'S',
  'major bets': 'E',
  'group betting': 'G',
}

export const PREVIOUS_RANGE_MAX_DAYS = 31

/** Lab `/betfair-bet-ticker` hard cap. */
export const BETFAIR_RANGE_MAX_DAYS = 15

/**
 * Lab Betfair Bet Ticker BQ switch: `getDiffDays() > 7 || !isSameDay(toDate)`.
 * Within 7 calendar days ending today → Postgres `ticker`; otherwise BigQuery `tickerBQ`.
 */
export const TICKER_BQ_SPAN_DAYS = 7

export const DEFAULT_TICKER_FILTERS: TickerFilters = {
  tmId: '',
  members: [],
  search: '',
  sport: 'all',
  event: '',
  market: '',
  marketType: 'all',
  side: 'all',
  status: 'all',
  currency: 'all',
  flagged: 'all',
  from: '',
  to: '',
  stakeMin: null,
  stakeMax: null,
  page: 1,
  pageSize: 25,
}

function localIsoDay(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseLocalDay(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return null
  const dt = new Date(y, m - 1, d)
  return Number.isNaN(dt.getTime()) ? null : dt
}

/** Default previous / flag-final / Betfair window: Today (Lab datepickers). */
export function defaultPreviousRange(now: Date = new Date()): Pick<TickerFilters, 'from' | 'to'> {
  const day = localIsoDay(now)
  return { from: day, to: day }
}

/**
 * Lab `/betfair-bet-ticker` → BigQuery when the span exceeds 7 days or `to` is not today.
 * Used by Bet Ticker Previous and Flag Final for `ticker` vs `tickerBQ`.
 */
export function shouldUseTickerBq(
  from: string,
  to: string,
  now: Date = new Date(),
): boolean {
  const fromDate = parseLocalDay(from)
  const toDate = parseLocalDay(to)
  if (!fromDate || !toDate) return false
  const diffDays = Math.floor(
    Math.abs(toDate.getTime() - fromDate.getTime()) / 86_400_000,
  )
  if (diffDays > TICKER_BQ_SPAN_DAYS) return true
  return localIsoDay(now) !== to
}

/** Lab `/bet-ticker-flag-final` — previous window + Flagged Final Bets. */
export function defaultFlagFinalFilters(): TickerFilters {
  return {
    ...DEFAULT_TICKER_FILTERS,
    ...defaultPreviousRange(),
    flagged: 'final',
  }
}
